import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// The written policy is 3 weeks' notice. The warning fires earlier, at 2.5 weeks,
// so a request that is only barely inside the policy window does not get the prompt.
const SHORT_NOTICE_PTO_DAYS = 2.5 * 7;

function calendarDaysUntil(isoDate) {
  const [year, month, day] = isoDate.split('-').map(Number);
  const start = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return (start.getTime() - today.getTime()) / (1000 * 60 * 60 * 24);
}

function isShortNoticePto(startDate) {
  if (!startDate) return false;
  return calendarDaysUntil(startDate) < SHORT_NOTICE_PTO_DAYS;
}

function parseLocalIso(iso) {
  const [year, month, day] = String(iso).split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function localIso(date) {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function holidayDatesFromPolicies(policies) {
  const dates = new Set();
  (policies || []).forEach(policy => {
    (policy.holidays || []).forEach(holiday => {
      const raw = typeof holiday === 'string' ? holiday : holiday && holiday.date;
      if (!raw) return;
      const iso = String(raw).trim().slice(0, 10);
      if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) dates.add(iso);
    });
  });
  return dates;
}

function isWeekendDate(date) {
  const day = date.getDay();
  return day === 0 || day === 6;
}

// Same rules as calculate_business_days_with_portions: weekends and company
// holidays are skipped, a PM start drops the morning, and an AM end drops
// the afternoon.
function previewRequestDays(startIso, endIso, holidays, startPortion, endPortion) {
  if (!startIso || !endIso) return null;
  const start = parseLocalIso(startIso);
  const end = parseLocalIso(endIso);
  if (!start || !end || start > end) return null;

  const valid = ['full', 'am', 'pm'];
  const sp = valid.includes(startPortion) ? startPortion : 'full';
  const ep = valid.includes(endPortion) ? endPortion : 'full';
  const holidaySet = holidays || new Set();

  const weekendDates = [];
  const holidayDates = [];
  const countedDates = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    const iso = localIso(cursor);
    if (isWeekendDate(cursor)) weekendDates.push(iso);
    else if (holidaySet.has(iso)) holidayDates.push(iso);
    else countedDates.push(iso);
    cursor.setDate(cursor.getDate() + 1);
  }

  const startIsBusiness = countedDates[0] === startIso;
  const endIsBusiness = countedDates[countedDates.length - 1] === endIso;
  let days = countedDates.length;

  if (startIso === endIso) {
    if (!startIsBusiness) days = 0;
    else if ((sp === 'full' && ep === 'full') || (sp === 'am' && ep === 'pm')) days = 1;
    else days = 0.5;
  } else if (days > 0) {
    if (sp === 'pm' && startIsBusiness) days -= 0.5;
    if (ep === 'am' && endIsBusiness) days -= 0.5;
    if (days < 0) days = 0;
  }

  return {
    days: Math.round(days * 100) / 100,
    weekendDates,
    holidayDates,
    countedDates,
    halfStart: startIso !== endIso && startIsBusiness && sp === 'pm',
    halfEnd: startIso !== endIso && endIsBusiness && ep === 'am',
    singleHalf: startIso === endIso && days === 0.5 ? (sp === 'pm' ? 'pm' : 'am') : null,
  };
}

function formatDayCount(days) {
  const rounded = Math.round(Number(days) * 100) / 100;
  if (!Number.isFinite(rounded)) return '—';
  const text = Number.isInteger(rounded) ? String(rounded) : String(rounded);
  return `${text} ${rounded === 1 ? 'day' : 'days'}`;
}

function joinWithAnd(items) {
  if (items.length <= 1) return items[0] || '';
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

function notCountedSentence(preview, formatDate) {
  const bits = [];
  if (preview.weekendDates.length > 0 && preview.weekendDates.length <= 2) {
    bits.push(joinWithAnd(preview.weekendDates.map(iso => (
      parseLocalIso(iso).toLocaleDateString(undefined, { weekday: 'long' })
    ))));
  } else if (preview.weekendDates.length > 2) {
    bits.push(`${preview.weekendDates.length} weekend days`);
  }

  if (preview.holidayDates.length === 1) {
    bits.push(`the company holiday on ${formatDate(preview.holidayDates[0])}`);
  } else if (preview.holidayDates.length > 1) {
    bits.push(`${preview.holidayDates.length} company holidays (${preview.holidayDates.map(formatDate).join(', ')})`);
  }

  if (bits.length === 0) return null;
  const onlyHoliday = bits.length === 1 && preview.holidayDates.length === 1 && preview.weekendDates.length === 0;
  const sentence = `${joinWithAnd(bits)} ${onlyHoliday ? 'is' : 'are'} not counted, so this can stay one request.`;
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}

function DurationChoice({ name, checked, onChange, title, detail }) {
  return (
    <label style={{
      display: 'flex',
      gap: 10,
      alignItems: 'flex-start',
      padding: '10px 12px',
      border: checked ? '1px solid var(--symbolica-black)' : '1px solid var(--symbolica-warm-border)',
      borderRadius: 8,
      background: checked ? '#F7F4EE' : 'transparent',
      cursor: 'pointer',
    }}>
      <input type="radio" name={name} checked={checked} onChange={onChange} style={{ marginTop: 3 }} />
      <span>
        <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--symbolica-black)' }}>{title}</span>
        <span style={{ display: 'block', fontSize: 13, lineHeight: 1.4, color: 'var(--symbolica-muted)', marginTop: 2 }}>{detail}</span>
      </span>
    </label>
  );
}

const PTOPage = ({ user }) => {
  const [ptoRequests, setPtoRequests] = useState([]);
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [requestType, setRequestType] = useState('pto'); // 'pto' or 'sick'
  const [ptoBalance, setPtoBalance] = useState({ total: 0, used: 0, remaining: 0, pending: 0 });
  const [showForm, setShowForm] = useState(false);
  const [showShortNotice, setShowShortNotice] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Duration is a tagged union so the form cannot represent invalid combinations
  // (e.g. PM-start + AM-end on a single-day request). The variant tracks whether
  // the selected date range is a single day or multiple days.
  //   { kind: 'single', portion: 'full' | 'am' | 'pm' }
  //   { kind: 'multi',  startPortion: 'full' | 'pm', endPortion: 'full' | 'am' }
  const [formData, setFormData] = useState({
    startDate: '',
    endDate: '',
    policy_id: '',
    reason: '',
    duration: { kind: 'single', portion: 'full' }
  });
  const [myPolicies, setMyPolicies] = useState([]);
  const [mySickPolicies, setMySickPolicies] = useState([]);
  const [policiesLoading, setPoliciesLoading] = useState(true);
  const [empRecord, setEmpRecord] = useState(null);
  const [sickBalance, setSickBalance] = useState({ total: 0, used: 0, pending: 0, remaining: 0 });
  const [balanceAdjustments, setBalanceAdjustments] = useState([]);

  // Holidays state
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [holidays, setHolidays] = useState([]);

  // Tooltip state for advance booking badge
  const [showTooltip, setShowTooltip] = useState(false);
  const [showCalendarTooltip, setShowCalendarTooltip] = useState(false);
  const [showAnniversaryTooltip, setShowAnniversaryTooltip] = useState(false);

  // Fetch PTO data from API
  const fetchPTOData = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/pto/requests`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();

      if (data.success) {
        const raw = data.ptoRequests ?? data.requests ?? [];
        // Sort newest → oldest by start date (or created date fallback)
        raw.sort((a, b) => {
          const aDate = new Date(a.startDate || a.start_date || a.created_at || a.createdAt || 0);
          const bDate = new Date(b.startDate || b.start_date || b.created_at || b.createdAt || 0);
          return bDate - aDate;
        });
        setPtoRequests(raw);
        const rawBal = data.ptoBalance ?? data.balance ?? {};
        const mergedBal = { total:0, used:0, remaining:0, pending:0, ...rawBal };
        setPtoBalance(mergedBal);
        setPage(1); // reset pagination on refresh
      } else {
        console.error('Failed to fetch PTO data:', data.error);
      }
    } catch (error) {
      console.error('Error fetching PTO data:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchPTOData();
    } else {
      // If no user is provided, don't stay in loading state
      setLoading(false);
    }
  }, [user, fetchPTOData]);

  // Fetch balance adjustments
  const fetchBalanceAdjustments = useCallback(async () => {
    if (!user) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/pto/adjustments`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setBalanceAdjustments(data.adjustments || []);
      } else {
        console.error('Failed to fetch balance adjustments:', data.error);
      }
    } catch (error) {
      console.error('Error fetching balance adjustments:', error);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchBalanceAdjustments();
    }
  }, [user, fetchBalanceAdjustments]);

  useEffect(() => {
    if(!user) return;

    const fetchPolicies = async () => {
      setPoliciesLoading(true);
      try {
        // Use employee-specific policies endpoint instead of admin endpoint
        const res = await fetch(`${API_BASE}/api/pto/policies`, {
          headers: {
            'Content-Type': 'application/json',
            'X-User-Email': user.email
          },
          credentials: 'include'
        });
        if (!res.ok) return;
        const pdata = await res.json();
        
        // find employee record for this user via public employees
        const er = await fetch(`${API_BASE}/api/org_chart/employees`, { credentials: 'include' });
        const edata = await er.json();
        const emp = edata.employees[user.email.toLowerCase()];
        
        if (emp) {
          setEmpRecord(emp);
          
          // The policies are already filtered to only those assigned to this employee
          const allAssignedPolicies = pdata.policies || [];
          
          // Separate policies by type
          const ptoPolicies = allAssignedPolicies.filter(p => 
            p.policy_type === 'pto' || !p.policy_type
          );
          
          const sickPolicies = allAssignedPolicies.filter(p => 
            p.policy_type === 'sick'
          );
          
          setMyPolicies(ptoPolicies);
          setMySickPolicies(sickPolicies);
          
          // Set default policy based on current request type
          if (requestType === 'pto' && ptoPolicies.length > 0) {
            setFormData(prev => ({ ...prev, policy_id: ptoPolicies[0].id || ptoPolicies[0].name }));
          } else if (requestType === 'sick' && sickPolicies.length > 0) {
            setFormData(prev => ({ ...prev, policy_id: sickPolicies[0].id || sickPolicies[0].name }));
          }
        }
      } catch (e) {
        console.error('Error fetching policies:', e);
      } finally {
        setPoliciesLoading(false);
      }
    };
    fetchPolicies();
  }, [user, requestType]);

  const fetchSickRequests = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/sick/requests`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const sickRequests = data.requests || [];
          // Update the existing requests state by filtering out sick time requests and adding the new ones
          setPtoRequests(prevRequests => {
            const sickPolicyIds = mySickPolicies.map(p => p.id || p.name);
            
            // Keep requests that are NOT sick time requests (to avoid duplicates)
            const nonSickRequests = prevRequests.filter(req => 
              !sickPolicyIds.includes(req.policy_id)
            );
            return [...nonSickRequests, ...sickRequests];
          });
          setSickBalance(data.sick_balance || { total: 0, used: 0, pending: 0, remaining: 0 });
        }
      }
    } catch (error) {
      console.error('Error fetching sick requests:', error);
    }
  };

  // Fetch sick time requests when requestType is 'sick' and policies are loaded
  useEffect(() => {
    if (user && requestType === 'sick' && mySickPolicies.length > 0) {
      fetchSickRequests();
    }
  }, [user, requestType, mySickPolicies.length]);

  // Default duration value for each variant. Whenever the form switches modes
  // (e.g. user extends a single-day range into a multi-day one), the duration
  // is replaced entirely so no stale fields from the other variant leak through.
  const defaultDurationFor = (kind) => kind === 'single'
    ? { kind: 'single', portion: 'full' }
    : { kind: 'multi', startPortion: 'full', endPortion: 'full' };

  // Flatten the duration union into the {start_portion, end_portion} pair the
  // backend expects. Single-day uses the same value for both, matching the
  // existing same-day rules in calculate_business_days_with_portions.
  const durationToPortions = (duration) => duration.kind === 'single'
    ? { start_portion: duration.portion, end_portion: duration.portion }
    : { start_portion: duration.startPortion, end_portion: duration.endPortion };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      // Auto-advance end when start moves past it so the picker never shows an inverted range
      if (name === 'startDate' && next.endDate && value > next.endDate) {
        next.endDate = value;
      }
      // If the date change crosses the single/multi boundary, swap the duration
      // variant. Replacing rather than mutating prevents impossible states like
      // PM-start + AM-end on the same calendar day.
      if (name === 'startDate' || name === 'endDate') {
        const prevKind = prev.startDate && prev.endDate
          ? (prev.startDate === prev.endDate ? 'single' : 'multi')
          : null;
        const nextKind = next.startDate && next.endDate
          ? (next.startDate === next.endDate ? 'single' : 'multi')
          : null;
        if (nextKind && nextKind !== prevKind) {
          next.duration = defaultDurationFor(nextKind);
        }
      }
      return next;
    });
  };

  const handleSubmit = async (e, { acknowledgedShortNotice = false } = {}) => {
    if (e && e.preventDefault) e.preventDefault();
    
    // Prevent duplicate submissions
    if (submitting) {
      return;
    }
    
    // Validate date range using string comparison (YYYY-MM-DD sorts lexicographically)
    // Avoids UTC-offset issues that arise from new Date('YYYY-MM-DD') parsing as UTC midnight
    if (formData.startDate > formData.endDate) {
      alert('Start date cannot be after end date. Please check your dates.');
      return;
    }

    // Guard against submitting before policies have loaded.
    // The submit button is disabled while policiesLoading, so this is a safety net.
    if (policiesLoading || !formData.policy_id) {
      return;
    }

    // Sick time is often same-week, so the advance-notice policy applies to PTO only.
    if (requestType === 'pto' && !acknowledgedShortNotice && isShortNoticePto(formData.startDate)) {
      setShowShortNotice(true);
      return;
    }

    setShowShortNotice(false);
    setSubmitting(true);
    
    try {
      // Use different endpoints for PTO vs sick time requests
      const endpoint = requestType === 'sick' 
        ? `${API_BASE}/api/sick/requests`
        : `${API_BASE}/api/pto/requests`;
      
      const { start_portion, end_portion } = durationToPortions(formData.duration);
      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          startDate: formData.startDate,
          endDate: formData.endDate,
          policy_id: formData.policy_id,
          reason: formData.reason,
          start_portion,
          end_portion
        })
      });

      const data = await response.json();

      if (data.success) {
        // Add new request to the list (handle both key variations)
        const newReq = data.ptoRequest ?? data.request;
        if (newReq) {
          setPtoRequests(prev => [newReq, ...prev]);
        }
        setFormData({ startDate: '', endDate: '', policy_id: '', reason: '', duration: { kind: 'single', portion: 'full' } });
        setShowForm(false);
        
        // Refresh data based on request type
        if (requestType === 'pto') {
          fetchPTOData();
        } else {
          // For sick time, refresh sick requests specifically
          fetchSickRequests();
        }
      } else {
        console.error(`Failed to create ${requestType} request:`, data.error);
        alert(data.error || `Failed to create ${requestType} request`);
      }
    } catch (error) {
      console.error(`Error creating ${requestType} request:`, error);
      alert(`Error creating ${requestType} request`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async (id) => {
    const confirmTxt = window.prompt('Type DELETE to permanently remove this request.');
    if (confirmTxt !== 'DELETE') return;
    try {
      const res = await fetch(`${API_BASE}/api/pto/requests/${id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      const data = await res.json();
      if (data.success) {
        // remove from list
        setPtoRequests(prev => prev.filter(r => r.id !== id));
        if (data.ptoBalance) {
          setPtoBalance(prev => ({ ...prev, ...data.ptoBalance }));
        }
      } else {
        alert(data.error || 'Failed to cancel');
      }
    } catch (e) { alert(e.message); }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'status-approved';
      case 'denied':
      case 'rejected':
        return 'status-denied';
      case 'pending': return 'status-pending';
      default: return 'status-default';
    }
  };

  // Extract holidays from user's policies for the selected year
  const extractHolidays = (policies, year) => {
    const allHolidays = [];
    
    policies.forEach(policy => {
      if (policy.holidays && Array.isArray(policy.holidays)) {
        policy.holidays.forEach(holiday => {
          // Holiday could be a date string or an object with date and name
          let holidayDate, holidayName;
          
          if (typeof holiday === 'string') {
            holidayDate = holiday;
            holidayName = 'Holiday';
          } else if (holiday.date) {
            holidayDate = holiday.date;
            holidayName = holiday.name || 'Holiday';
          } else {
            return; // Skip invalid holiday entries
          }
          
          try {
            // Parse as local date to avoid timezone issues
            // Split the date string and create a local date
            const [dateYear, month, day] = holidayDate.split('-').map(num => parseInt(num, 10));
            const date = new Date(dateYear, month - 1, day); // month is 0-indexed
            if (date.getFullYear() === year) {
              allHolidays.push({
                date: holidayDate,
                name: holidayName,
                policy: policy.name
              });
            }
          } catch (e) {
            console.warn('Invalid holiday date:', holidayDate, e);
          }
        });
      }
    });
    
    // Sort holidays by date
    allHolidays.sort((a, b) => new Date(a.date) - new Date(b.date));
    
    return allHolidays;
  };

  // Update holidays when policies or selected year changes
  useEffect(() => {
    if (myPolicies.length > 0) {
      const yearHolidays = extractHolidays(myPolicies, selectedYear);
      setHolidays(yearHolidays);
    } else {
      setHolidays([]);
    }
  }, [myPolicies, selectedYear]);

  // Reset page when switching tabs
  useEffect(() => {
    setPage(1);
  }, [activeTab]);

  // Reset page when switching request types
  useEffect(() => {
    setPage(1);
  }, [requestType]);

  // Helper function to format dates properly (avoiding timezone issues)
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      // Handle both date strings and datetime strings
      if (dateStr.includes('T')) {
        // This is a datetime string, format it normally
        return new Date(dateStr).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
      } else {
        // This is a date string, parse as local date to avoid timezone issues
        const [year, month, day] = dateStr.split('-').map(num => parseInt(num, 10));
        const date = new Date(year, month - 1, day); // month is 0-indexed
        return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
      }
    } catch {
      return dateStr;
    }
  };

  // Helper function to separate requests into upcoming and past
  const separateRequests = (requests) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day for comparison
    
    const upcoming = [];
    const past = [];
    
    requests.forEach(req => {
      const startDate = req.startDate || req.start_date;
      if (startDate) {
        try {
          const [year, month, day] = startDate.split('-').map(num => parseInt(num, 10));
          const reqDate = new Date(year, month - 1, day);
          reqDate.setHours(0, 0, 0, 0);
          
          if (reqDate >= today) {
            upcoming.push(req);
          } else {
            past.push(req);
          }
        } catch {
          // If date parsing fails, default to past
          past.push(req);
        }
      } else {
        past.push(req);
      }
    });
    
    return { upcoming, past };
  };

  // Helper function to filter requests based on request type (PTO vs Sick Time)
  const filterRequestsByType = (requests) => {
    if (requestType === 'pto') {
      // Show requests associated with PTO policies
      const ptoPolicyIds = myPolicies.map(p => p.id || p.name);
      return requests.filter(req => 
        ptoPolicyIds.includes(req.policy_id) || !req.policy_id // Include requests without policy_id for backward compatibility
      );
    } else if (requestType === 'sick') {
      // Show requests associated with sick time policies
      const sickPolicyIds = mySickPolicies.map(p => p.id || p.name);
      return requests.filter(req => 
        sickPolicyIds.includes(req.policy_id)
      );
    }
    return requests;
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>;
  }

  // If no user is available, show a message instead of staying in loading
  if (!user) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Please log in to view your PTO information.</div>;
  }

  // Sick requests use the employee's PTO policy holidays, matching the backend.
  const requestHolidayDates = requestType === 'sick'
    ? holidayDatesFromPolicies(myPolicies)
    : holidayDatesFromPolicies(myPolicies.filter(p => (p.id || p.name) === formData.policy_id));
  const requestPortions = durationToPortions(formData.duration);
  const requestPreview = formData.startDate && formData.endDate
    ? previewRequestDays(
        formData.startDate,
        formData.endDate,
        requestHolidayDates,
        requestPortions.start_portion,
        requestPortions.end_portion
      )
    : null;
  const activeBalance = requestType === 'pto' ? ptoBalance : sickBalance;
  const balanceNow = Number(activeBalance.remaining);
  const showBalanceProjection = requestPreview && !activeBalance.is_usage_only && Number.isFinite(balanceNow);
  const balanceAfter = showBalanceProjection
    ? Math.round((balanceNow - requestPreview.days) * 100) / 100
    : null;
  const pendingDays = Number(activeBalance.pending);

  return (
    <div className="page-wrap">
      {/* Request Type Tabs */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', gap: '1rem', borderBottom: '1px solid #e5e7eb' }}>
          <button
            onClick={() => setRequestType('pto')}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'none',
              border: 'none',
              borderBottom: requestType === 'pto' ? '2px solid var(--symbolica-black)' : '2px solid transparent',
              color: requestType === 'pto' ? 'var(--symbolica-black)' : 'var(--symbolica-muted)',
              fontWeight: requestType === 'pto' ? '600' : '400',
              cursor: 'pointer',
              fontSize: '1rem'
            }}
          >
            PTO
          </button>
          {mySickPolicies.length > 0 && (
            <button
              onClick={() => setRequestType('sick')}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'none',
                border: 'none',
                borderBottom: requestType === 'sick' ? '2px solid var(--symbolica-black)' : '2px solid transparent',
                color: requestType === 'sick' ? 'var(--symbolica-black)' : 'var(--symbolica-muted)',
                fontWeight: requestType === 'sick' ? '600' : '400',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Sick Leave
            </button>
          )}
        </div>
      </div>

      {/* Balance Display - Only show for PTO */}
      {requestType === 'pto' && (() => {
        const stats = [
          { label: 'Balance', value: ptoBalance.remaining, sub: `of ${ptoBalance.total} days` },
          ptoBalance.allow_full_year_upfront && ptoBalance.accrued_to_date != null && ptoBalance.accrued_to_date !== ptoBalance.total
            ? { label: 'Accrued', value: ptoBalance.accrued_to_date, sub: 'to date' }
            : null,
          { label: 'Used', value: ptoBalance.used, sub: 'days' },
          { label: 'Pending', value: ptoBalance.pending, sub: 'days' },
          ptoBalance.utilization_pct !== null
            ? { label: 'Utilisation', value: `${ptoBalance.utilization_pct}%`, sub: 'workdays' }
            : null,
        ].filter(Boolean);

        const tags = [
          ptoBalance.allow_full_year_upfront && 'Advance booking',
          ptoBalance.has_calendar_year && 'Calendar year',
          ptoBalance.has_anniversary_cycle && 'Anniversary cycle',
        ].filter(Boolean);

        return (
          <div style={{ marginBottom: 28 }}>
            {/* Stat cards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(auto-fit, minmax(140px, 1fr))`,
              gap: 12,
              marginBottom: tags.length > 0 ? 12 : 0,
            }}>
              {stats.map((s, i) => (
                <div key={i} style={{
                  background: 'var(--symbolica-card)',
                  border: '1px solid var(--symbolica-warm-border)',
                  borderRadius: 'var(--radius)',
                  padding: '18px 20px',
                }}>
                  <div style={{
                    fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 400, textTransform: 'uppercase',
                    letterSpacing: '0.06em', color: 'var(--symbolica-muted)', marginBottom: 6,
                  }}>
                    {s.label}
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 600, color: 'var(--symbolica-black)', lineHeight: 1, marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.value}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--symbolica-muted)' }}>{s.sub}</div>
                </div>
              ))}
            </div>

            {/* Tags */}
            {tags.length > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {tags.map(t => (
                  <span key={t} style={{
                    fontSize: 11, fontWeight: 500, color: 'var(--symbolica-muted)',
                    background: '#F0EDE6', borderRadius: 4, padding: '3px 10px',
                  }}>
                    {t}
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Sick Time Balance Display */}
      {requestType === 'sick' && mySickPolicies.length > 0 && (
        <div style={{
          background: '#D7DFF5',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '2rem',
          color: 'var(--symbolica-black)',
          boxShadow: 'none',
          cursor: 'default'
        }}>
          <h2 style={{ margin: '0 0 1rem 0', fontSize: '1.25rem', fontWeight: '600' }}>
            Sick Leave
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '1rem' }}>
            {sickBalance.is_usage_only ? (
              // Usage-only policy display (e.g., UK SSP) - only show Used and Pending
              <>
                <div>
                  <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>Used</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>{sickBalance.used}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>Pending</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>{sickBalance.pending}</div>
                </div>
              </>
            ) : (
              // Accrual-based policy display (e.g., US Paid Sick Leave)
              <>
                <div>
                  <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>Balance</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>{sickBalance.remaining}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>Used</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>{sickBalance.used}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', opacity: 0.8 }}>Pending</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>{sickBalance.pending}</div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Sick Time Notice */}
      {requestType === 'sick' && (
        <div style={{
          background: '#fef3c7',
          border: '1px solid #f59e0b',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '2rem'
        }}>
          <p style={{ margin: 0, color: '#92400e', fontSize: '0.875rem' }}>
            <strong>Note:</strong> {sickBalance.is_usage_only ? 
              'This is a usage-only policy - you have a fixed allowance that does not accrue over time.' :
              'Sick time is tracked for compliance purposes only and does not affect your PTO balance or utilization metrics.'
            }
          </p>
        </div>
      )}

      {/* New Request Button */}
      <div style={{ marginBottom: '2rem' }}>
        <button
          onClick={() => setShowForm(true)}
          disabled={requestType === 'pto' ? myPolicies.length === 0 : mySickPolicies.length === 0}
          style={{
            background: (requestType === 'pto' ? myPolicies.length === 0 : mySickPolicies.length === 0)
              ? 'var(--symbolica-warm-border)' : 'var(--symbolica-accent)',
            color: 'var(--symbolica-black)',
            border: 'none',
            borderRadius: '8px',
            padding: '0.75rem 1.5rem',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: (requestType === 'pto' ? myPolicies.length === 0 : mySickPolicies.length === 0)
              ? 'not-allowed' : 'pointer',
            boxShadow: 'none'
          }}
        >
          + New {requestType === 'pto' ? 'PTO' : 'Sick Time'} Request
        </button>
        {(requestType === 'pto' ? myPolicies.length === 0 : mySickPolicies.length === 0) && (
          <p style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: 'var(--symbolica-muted)' }}>
            No {requestType === 'pto' ? 'PTO' : 'sick time'} policies assigned to your account.
          </p>
        )}
      </div>

      {/* New Request Form Modal — portalled to cover header/sidebar */}
      {showForm && createPortal(
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(34,34,34,0.35)',
            backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowForm(false); }}
        >
          <div style={{
            background: 'var(--symbolica-white)',
            borderRadius: '12px',
            width: '94%',
            maxWidth: '780px',
            maxHeight: '92vh',
            overflow: 'auto',
            boxShadow: '0 24px 64px rgba(0,0,0,0.18)',
          }}>
            {/* Header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '16px 16px',
              borderBottom: '1px solid var(--symbolica-warm-border)',
              position: 'sticky', top: 0, background: 'var(--symbolica-white)', zIndex: 1,
              borderRadius: '12px 12px 0 0',
            }}>
              <h3 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: '1.2rem', fontWeight: 400, color: 'var(--symbolica-black)' }}>
                New {requestType === 'pto' ? 'PTO' : 'Sick Time'} Request
              </h3>
              <button type="button" onClick={() => setShowForm(false)}
                style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--symbolica-muted)', cursor: 'pointer', padding: '4px 8px', lineHeight: 1, borderRadius: 4 }}>
                &times;
              </button>
            </div>

            <div style={{ padding: '20px 16px' }}>

            {/* Inline Calendar */}
            {(() => {
              const today = new Date();
              const calMonth = formData._calMonth != null ? formData._calMonth : today.getMonth();
              const calYear = formData._calYear != null ? formData._calYear : today.getFullYear();
              const DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
              const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

              const firstDay = new Date(calYear, calMonth, 1);
              let startDow = firstDay.getDay() - 1;
              if (startDow < 0) startDow = 6;
              const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();

              const cells = [];
              for (let i = 0; i < startDow; i++) cells.push(null);
              for (let d = 1; d <= daysInMonth; d++) cells.push(d);

              const fmt = (d) => `${calYear}-${String(calMonth+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
              const startD = formData.startDate;
              const endD = formData.endDate;

              const isInRange = (d) => {
                if (!d || !startD) return false;
                const ds = fmt(d);
                if (!endD) return ds === startD;
                return ds >= startD && ds <= endD;
              };
              const isStart = (d) => d && fmt(d) === startD;
              const isEnd = (d) => d && fmt(d) === endD;
              const isWeekend = (d) => { if (!d) return false; const dow = new Date(calYear, calMonth, d).getDay(); return dow === 0 || dow === 6; };
              const isPast = (d) => { if (!d) return false; const dt = new Date(calYear, calMonth, d); dt.setHours(0,0,0,0); const t = new Date(); t.setHours(0,0,0,0); return dt < t; };
              const isToday = (d) => d && fmt(d) === `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;

              const handleDayClick = (d) => {
                if (!d || isWeekend(d) || isPast(d)) return;
                const ds = fmt(d);
                setFormData(prev => {
                  const next = { ...prev };
                  if (!prev.startDate || (prev.startDate && prev.endDate) || ds < prev.startDate) {
                    next.startDate = ds;
                    next.endDate = '';
                  } else {
                    next.endDate = ds;
                  }
                  // The calendar writes dates directly, so it has to swap the
                  // duration variant the same way the hidden inputs do.
                  const prevKind = prev.startDate && prev.endDate
                    ? (prev.startDate === prev.endDate ? 'single' : 'multi')
                    : null;
                  const nextKind = next.startDate && next.endDate
                    ? (next.startDate === next.endDate ? 'single' : 'multi')
                    : null;
                  if (nextKind && nextKind !== prevKind) {
                    next.duration = defaultDurationFor(nextKind);
                  }
                  return next;
                });
              };

              const prevMonth = () => {
                const m = calMonth === 0 ? 11 : calMonth - 1;
                const y = calMonth === 0 ? calYear - 1 : calYear;
                setFormData(prev => ({ ...prev, _calMonth: m, _calYear: y }));
              };
              const nextMonth = () => {
                const m = calMonth === 11 ? 0 : calMonth + 1;
                const y = calMonth === 11 ? calYear + 1 : calYear;
                setFormData(prev => ({ ...prev, _calMonth: m, _calYear: y }));
              };

              return (
                <div style={{ marginBottom: 20 }}>
                  {/* Month nav */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                    <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--symbolica-black)' }}>
                      {MONTHS[calMonth]} {calYear}
                    </div>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button type="button" onClick={prevMonth} className="secondary-button" style={{ padding: '4px 10px', fontSize: 14 }}>&lsaquo;</button>
                      <button type="button" onClick={nextMonth} className="secondary-button" style={{ padding: '4px 10px', fontSize: 14 }}>&rsaquo;</button>
                    </div>
                  </div>

                  {/* Day headers */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0 }}>
                    {DAYS.map(d => (
                      <div key={d} style={{ textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 400, color: 'var(--symbolica-muted)', padding: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{d}</div>
                    ))}
                  </div>

                  {/* Date grid */}
                  <div style={{ border: '1px solid var(--symbolica-warm-border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}>
                    {(() => {
                      const weeks = [];
                      for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
                      return weeks.map((week, wi) => (
                        <div key={wi} style={{
                          display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)',
                          borderBottom: wi < weeks.length - 1 ? '1px solid var(--symbolica-warm-border)' : 'none',
                        }}>
                          {week.map((d, di) => {
                            const weekend = isWeekend(d);
                            const past = isPast(d);
                            const disabled = !d || weekend || past;
                            const inRange = isInRange(d);
                            const start = isStart(d);
                            const end = isEnd(d);
                            const todayCell = isToday(d);
                            return (
                              <div key={di}
                                onClick={() => !disabled && handleDayClick(d)}
                                style={{
                                  textAlign: 'center',
                                  padding: '12px 0',
                                  fontSize: 13,
                                  fontWeight: (start || end) ? 600 : 400,
                                  cursor: disabled ? 'default' : 'pointer',
                                  color: disabled ? '#C8C4BC' : 'var(--symbolica-black)',
                                  background: (start || end) ? 'var(--symbolica-accent)' : inRange ? '#EDEAF8' : 'transparent',
                                  borderLeft: di > 0 ? '1px solid var(--symbolica-warm-border)' : 'none',
                                  position: 'relative',
                                  transition: 'background 0.1s',
                                }}
                              >
                                {d || ''}
                                {todayCell && <div style={{ position: 'absolute', bottom: 4, left: '50%', transform: 'translateX(-50%)', width: 4, height: 4, borderRadius: '50%', background: 'var(--symbolica-periwinkle)' }} />}
                              </div>
                            );
                          })}
                        </div>
                      ));
                    })()}
                  </div>

                  {/* Selection summary */}
                  <div style={{ display: 'flex', gap: 12, marginTop: 14 }}>
                    <div style={{ flex: 1, background: '#F0EDE6', borderRadius: 6, padding: '8px 12px', fontSize: 13 }}>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 400, color: 'var(--symbolica-muted)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Start</div>
                      {startD || <span style={{ color: 'var(--symbolica-muted)' }}>Click a date</span>}
                    </div>
                    <div style={{ flex: 1, background: '#F0EDE6', borderRadius: 6, padding: '8px 12px', fontSize: 13 }}>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 400, color: 'var(--symbolica-muted)', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.04em' }}>End</div>
                      {endD || <span style={{ color: 'var(--symbolica-muted)' }}>Click a date</span>}
                    </div>
                  </div>

                </div>
              );
            })()}

            {!formData.startDate && (
              <p style={{ margin: '0 0 18px', fontSize: 14, lineHeight: 1.45, color: 'var(--symbolica-black)' }}>
                Pick a start and end date. Weekends and company holidays in between are not counted, so Thursday through the following Tuesday can be one request.
              </p>
            )}
            {formData.startDate && !formData.endDate && (
              <p style={{ margin: '0 0 18px', fontSize: 14, lineHeight: 1.45, color: 'var(--symbolica-black)' }}>
                Now click the last day. Saturday and Sunday in between will not be deducted from your balance.
              </p>
            )}
            {requestPreview && (
              <div style={{
                marginBottom: 18,
                border: '1px solid var(--symbolica-warm-border)',
                borderRadius: 8,
                padding: '14px 16px',
                background: '#F7F4EE',
              }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--symbolica-muted)', marginBottom: 6 }}>
                  This request
                </div>
                <div style={{ fontSize: 28, fontWeight: 600, lineHeight: 1, color: 'var(--symbolica-black)', marginBottom: 6 }}>
                  {formatDayCount(requestPreview.days)}
                </div>
                <div style={{ fontSize: 14, color: 'var(--symbolica-black)', marginBottom: 8 }}>
                  {formatDate(formData.startDate)}
                  {formData.startDate !== formData.endDate ? ` – ${formatDate(formData.endDate)}` : ''}
                </div>
                {requestPreview.countedDates.length > 0 ? (
                  <div style={{ fontSize: 13, lineHeight: 1.45, color: 'var(--symbolica-black)', marginBottom: 8 }}>
                    Counted: {requestPreview.countedDates.map(iso => {
                      const label = parseLocalIso(iso).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
                      if (requestPreview.halfStart && iso === requestPreview.countedDates[0]) return `${label} (leave in the afternoon)`;
                      if (requestPreview.halfEnd && iso === requestPreview.countedDates[requestPreview.countedDates.length - 1]) return `${label} (back in the afternoon)`;
                      if (requestPreview.singleHalf === 'am') return `${label} (morning only)`;
                      if (requestPreview.singleHalf === 'pm') return `${label} (afternoon only)`;
                      return label;
                    }).join(' · ')}
                  </div>
                ) : (
                  <div style={{ fontSize: 13, lineHeight: 1.45, color: 'var(--symbolica-black)', marginBottom: 8 }}>
                    Nothing in this range is a workday.
                  </div>
                )}
                <p style={{ margin: 0, fontSize: 13, lineHeight: 1.45, color: 'var(--symbolica-black)' }}>
                  {notCountedSentence(requestPreview, formatDate)
                    || 'Weekends and company holidays are not counted. A stretch that runs through a weekend can stay one request.'}
                </p>
                {showBalanceProjection && (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 12,
                    marginTop: 12,
                    paddingTop: 12,
                    borderTop: '1px solid var(--symbolica-warm-border)',
                  }}>
                    <div>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--symbolica-muted)', marginBottom: 4 }}>
                        Balance now
                      </div>
                      <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--symbolica-black)' }}>{formatDayCount(balanceNow)}</div>
                    </div>
                    <div>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--symbolica-muted)', marginBottom: 4 }}>
                        If approved
                      </div>
                      <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--symbolica-black)' }}>{formatDayCount(balanceAfter)} left</div>
                    </div>
                  </div>
                )}
                {showBalanceProjection && pendingDays > 0 && (
                  <p style={{ margin: '10px 0 0', fontSize: 12, lineHeight: 1.4, color: 'var(--symbolica-muted)' }}>
                    This does not include {formatDayCount(pendingDays)} already pending.
                  </p>
                )}
                {showBalanceProjection && balanceAfter < 0 && (
                  <p style={{ margin: '10px 0 0', fontSize: 13, lineHeight: 1.4, color: 'var(--symbolica-black)' }}>
                    This is more than your current balance.
                  </p>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              <input type="hidden" name="startDate" value={formData.startDate} />
              <input type="hidden" name="endDate" value={formData.endDate} />
              <input type="hidden" name="policy_id" value={formData.policy_id} />
              {/* Half-day controls — render directly from the duration variant
                  so the UI can only emit values that are valid for the current mode.
                  PM on the first day is the afternoon they leave; AM on the last
                  day is the morning before they come back. */}
              {formData.startDate && formData.endDate && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--symbolica-black)' }}>
                    How much of each day
                  </div>
                  {formData.duration.kind === 'single' ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <DurationChoice
                        name="singleDayPortion"
                        checked={formData.duration.portion === 'full'}
                        onChange={() => setFormData(prev => ({ ...prev, duration: { kind: 'single', portion: 'full' } }))}
                        title="Full day off"
                        detail="You are out the whole day."
                      />
                      <DurationChoice
                        name="singleDayPortion"
                        checked={formData.duration.portion === 'am'}
                        onChange={() => setFormData(prev => ({ ...prev, duration: { kind: 'single', portion: 'am' } }))}
                        title="Morning off"
                        detail="You are off in the morning and back in the afternoon."
                      />
                      <DurationChoice
                        name="singleDayPortion"
                        checked={formData.duration.portion === 'pm'}
                        onChange={() => setFormData(prev => ({ ...prev, duration: { kind: 'single', portion: 'pm' } }))}
                        title="Afternoon off"
                        detail="You are in the office in the morning and off in the afternoon."
                      />
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--symbolica-black)' }}>
                          First day, {formatDate(formData.startDate)}
                        </div>
                        <DurationChoice
                          name="startDayPortion"
                          checked={formData.duration.startPortion === 'full'}
                          onChange={() => setFormData(prev => ({
                            ...prev,
                            duration: { ...prev.duration, startPortion: 'full' }
                          }))}
                          title="Full day off"
                          detail="You are out from the start of the day."
                        />
                        <DurationChoice
                          name="startDayPortion"
                          checked={formData.duration.startPortion === 'pm'}
                          onChange={() => setFormData(prev => ({
                            ...prev,
                            duration: { ...prev.duration, startPortion: 'pm' }
                          }))}
                          title="Leave in the afternoon"
                          detail="You work that morning, then your time off starts."
                        />
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--symbolica-black)' }}>
                          Last day, {formatDate(formData.endDate)}
                        </div>
                        <DurationChoice
                          name="endDayPortion"
                          checked={formData.duration.endPortion === 'full'}
                          onChange={() => setFormData(prev => ({
                            ...prev,
                            duration: { ...prev.duration, endPortion: 'full' }
                          }))}
                          title="Full day off"
                          detail="You are out the whole day."
                        />
                        <DurationChoice
                          name="endDayPortion"
                          checked={formData.duration.endPortion === 'am'}
                          onChange={() => setFormData(prev => ({
                            ...prev,
                            duration: { ...prev.duration, endPortion: 'am' }
                          }))}
                          title="Back in the afternoon"
                          detail="You are off that morning and back in the office after lunch."
                        />
                      </div>
                    </div>
                  )}
                  {requestPreview && formData.duration.kind === 'multi' && formData.duration.startPortion === 'pm' && !requestPreview.halfStart && (
                    <p style={{ margin: 0, fontSize: 13, lineHeight: 1.4, color: 'var(--symbolica-muted)' }}>
                      The first day is a company holiday, so leaving in the afternoon does not change the total.
                    </p>
                  )}
                  {requestPreview && formData.duration.kind === 'multi' && formData.duration.endPortion === 'am' && !requestPreview.halfEnd && (
                    <p style={{ margin: 0, fontSize: 13, lineHeight: 1.4, color: 'var(--symbolica-muted)' }}>
                      The last day is a company holiday, so coming back in the afternoon does not change the total.
                    </p>
                  )}
                </div>
              )}
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: 'var(--symbolica-black)', marginBottom: '4px' }}>
                  Reason
                </label>
                <textarea
                  name="reason"
                  value={formData.reason}
                  onChange={handleInputChange}
                  required
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                  placeholder={requestType === 'pto' 
                    ? "Please provide a reason for your time off request..." 
                    : "Please provide a brief reason for your sick leave..."
                  }
                />
              </div>
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px',
                marginTop: '8px',
                paddingTop: '18px',
                borderTop: '1px solid var(--symbolica-warm-border)',
              }}>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="secondary-button"
                  style={{ padding: '10px 20px' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || policiesLoading}
                  className="primary-button"
                  style={{
                    padding: '10px 24px',
                    opacity: (submitting || policiesLoading) ? 0.5 : 1,
                    cursor: (submitting || policiesLoading) ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  {policiesLoading ? 'Loading...' : submitting ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showShortNotice && createPortal(
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 10001,
            background: 'rgba(34,34,34,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '16px',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowShortNotice(false); }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="short-notice-title"
            style={{
              background: 'var(--symbolica-white)',
              borderRadius: '12px',
              width: '94%',
              maxWidth: '480px',
              boxShadow: '0 24px 64px rgba(0,0,0,0.22)',
              padding: '24px 24px 20px',
            }}
          >
            <h3 id="short-notice-title" style={{ margin: '0 0 12px', fontFamily: 'var(--font-display)', fontSize: '1.25rem', fontWeight: 400, color: 'var(--symbolica-black)' }}>
              This time off is not guaranteed
            </h3>
            <p style={{ margin: '0 0 12px', fontSize: '15px', lineHeight: 1.5, color: 'var(--symbolica-black)' }}>
              Our policy is that this form is for requests 3 or more weeks out. Yours starts sooner than that, so it is not guaranteed you will get this time off.
            </p>
            <p style={{ margin: '0 0 20px', fontSize: '15px', lineHeight: 1.5, color: 'var(--symbolica-black)' }}>
              Talk to your manager directly as soon as you can. If they approve it, it still needs to be logged here.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                onClick={() => setShowShortNotice(false)}
                className="secondary-button"
                style={{ padding: '10px 20px' }}
              >
                Go back
              </button>
              <button
                type="button"
                onClick={() => handleSubmit(null, { acknowledgedShortNotice: true })}
                className="primary-button"
                style={{ padding: '10px 20px' }}
              >
                Submit anyway
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* PTO Requests List */}
      <div>
        <h2 className="content-title">
          Your {requestType === 'pto' ? 'PTO' : 'Sick Time'}
        </h2>
        
        {/* Tab Navigation */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: '16px' }}>
          <button
            onClick={() => setActiveTab('upcoming')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              color: activeTab === 'upcoming' ? 'var(--symbolica-primary)' : 'var(--symbolica-muted)',
              borderBottom: activeTab === 'upcoming' ? '2px solid var(--symbolica-primary)' : '2px solid transparent'
            }}
          >
            Upcoming
          </button>
          <button
            onClick={() => setActiveTab('past')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              color: activeTab === 'past' ? 'var(--symbolica-primary)' : 'var(--symbolica-muted)',
              borderBottom: activeTab === 'past' ? '2px solid var(--symbolica-primary)' : '2px solid transparent'
            }}
          >
            Past
          </button>
          <button
            onClick={() => setActiveTab('adjustments')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              color: activeTab === 'adjustments' ? 'var(--symbolica-primary)' : 'var(--symbolica-muted)',
              borderBottom: activeTab === 'adjustments' ? '2px solid var(--symbolica-primary)' : '2px solid transparent'
            }}
          >
            Admin Adjustments
          </button>
        </div>

        {(() => {
          // Show adjustments tab content if selected
          if (activeTab === 'adjustments') {
            if (balanceAdjustments.length === 0) {
              return (
                <div style={{ textAlign: 'center', padding: '32px', color: 'var(--symbolica-muted)' }}>
                  No balance adjustments have been made to your account.
                </div>
              );
            }
            
            return (
              <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>
                {balanceAdjustments.map(adj => (
                  <div key={adj.id} style={{ 
                    border:'1px solid #e5e7eb', 
                    borderRadius:8, 
                    padding:'16px', 
                    display:'flex', 
                    flexDirection:'column', 
                    gap:8 
                  }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                      <div>
                        <div style={{ fontWeight:600, fontSize:'16px', color: adj.adjustment_days > 0 ? '#059669' : '#dc2626' }}>
                          {adj.adjustment_days > 0 ? '+' : ''}{adj.adjustment_days} day{Math.abs(adj.adjustment_days) === 1 ? '' : 's'}
                        </div>
                        <div style={{ fontSize:'14px', color:'var(--symbolica-muted)', marginTop:4 }}>
                          {adj.policy_name} ({adj.policy_type === 'sick' ? 'Sick Time' : 'PTO'})
                        </div>
                      </div>
                      <div style={{ 
                        padding:'4px 12px', 
                        borderRadius:4, 
                        fontSize:'12px',
                        fontWeight:500,
                        background: adj.adjustment_days > 0 ? '#d1fae5' : '#fee2e2',
                        color: adj.adjustment_days > 0 ? '#065f46' : '#991b1b'
                      }}>
                        {adj.adjustment_days > 0 ? 'Credit' : 'Debit'}
                      </div>
                    </div>
                    
                    <div style={{ fontSize:'14px', color:'var(--symbolica-black)', marginTop:8 }}>
                      <strong>Reason:</strong> {adj.reason || 'No reason provided'}
                    </div>
                    
                    <div style={{ display:'flex', gap:16, marginTop:8 }}>
                      <div style={{ fontSize:'12px', color:'var(--symbolica-muted)' }}>
                        <strong>Date:</strong> {formatDate(adj.adjustment_date)}
                      </div>
                      <div style={{ fontSize:'12px', color:'var(--symbolica-muted)' }}>
                        <strong>By:</strong> {adj.created_by || 'Admin'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
          }
          
          // First filter requests by type (PTO vs Sick Time)
          const filteredRequests = filterRequestsByType(ptoRequests);
          
          if (filteredRequests.length === 0) {
            return (
              <div style={{ textAlign: 'center', padding: '32px', color: 'var(--symbolica-muted)' }}>
                No {requestType === 'pto' ? 'PTO' : 'sick time'} requests found. Create your first request above.
              </div>
            );
          }
          
          return (
            <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>
              {(() => {
                // Separate filtered requests by date
                const { upcoming, past } = separateRequests(filteredRequests);
                const currentRequests = activeTab === 'upcoming' ? upcoming : past;
                
                if (currentRequests.length === 0) {
                  return (
                    <div style={{ textAlign: 'center', padding: '32px', color: 'var(--symbolica-muted)' }}>
                      No {activeTab} {requestType === 'pto' ? 'PTO' : 'sick time'} requests found.
                    </div>
                  );
                }
                
                const perPage = 5;
                const startIdx = (page - 1) * perPage;
                const visible = currentRequests.slice(startIdx, startIdx + perPage);
                
                return (
                  <>
                    {visible.map(req => {
                      const start = req.startDate || req.start_date;
                      const end = req.endDate || req.end_date;
                      const submittedAt = req.created_at || req.createdAt || '—';
                      
                      return (
                        <div key={req.id} style={{
                          border: '1px solid var(--symbolica-warm-border)',
                          borderRadius: 'var(--radius)',
                          padding: '16px 20px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          flexWrap: 'wrap',
                          gap: 16,
                          background: 'var(--symbolica-card)',
                        }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                              <span style={{ fontWeight: 600, fontSize: 14 }}>{formatDate(start)} &rarr; {formatDate(end)}</span>
                              <span style={{ fontSize: 12, color: 'var(--symbolica-muted)' }}>{req.days} day{req.days === 1 ? '' : 's'}</span>
                            </div>
                            <div style={{ fontSize: 13, color: 'var(--symbolica-muted)' }}>{req.reason || '\u2014'}</div>
                            <div style={{ fontSize: 12, color: '#B0AFA8', marginTop: 2 }}>Submitted {formatDate(submittedAt)}</div>
                            {req.status !== 'approved' && req.status !== 'rejected' && req.status !== 'denied' && (
                              <div style={{ marginTop: 8 }}>
                                <button className="danger-button" style={{ fontSize: 12, padding: '5px 14px' }} onClick={() => handleCancel(req.id)}>Cancel Request</button>
                              </div>
                            )}
                          </div>
                          <span className={getStatusColor(req.status || 'pending')} style={{ textTransform: 'capitalize', flexShrink: 0 }}>{req.status}</span>
                        </div>
                      );
                    })}
                    
                    {/* Pagination Controls */}
                    {currentRequests.length > 5 && (
                      <div style={{ display:'flex', justifyContent:'center', gap:8, marginTop:16 }}>
                        <button className="secondary-button" disabled={page===1} onClick={()=>setPage(p=>Math.max(1,p-1))}>Prev</button>
                        <span style={{ fontSize:14, alignSelf:'center' }}>{page} / {Math.ceil(currentRequests.length/5)}</span>
                        <button className="secondary-button" disabled={page>=Math.ceil(currentRequests.length/5)} onClick={()=>setPage(p=>p+1)}>Next</button>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          );
        })()}
      </div>

      {/* Observed Holidays Section - Only show for PTO requests */}
      {requestType === 'pto' && empRecord && myPolicies.length > 0 && (
        <div style={{ margin:'32px 0' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, flexWrap:'wrap', gap:12 }}>
            <h2 className="content-title">Observed Holidays</h2>
            <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
              <button 
                className="secondary-button" 
                onClick={() => setSelectedYear(year => year - 1)}
                style={{ padding:'4px 8px', fontSize:'14px' }}
              >
                ← {selectedYear - 1}
              </button>
              <span style={{ fontSize:'16px', fontWeight:'600', minWidth:'60px', textAlign:'center' }}>
                {selectedYear}
              </span>
              <button 
                className="secondary-button" 
                onClick={() => setSelectedYear(year => year + 1)}
                style={{ padding:'4px 8px', fontSize:'14px' }}
              >
                {selectedYear + 1} →
              </button>
            </div>
          </div>
          
          <div style={{ border:'1px solid #e5e7eb', borderRadius:8, padding:16, background:'#fff' }}>
            {holidays.length === 0 ? (
              <div style={{ textAlign:'center', padding:'32px', color:'var(--symbolica-muted)' }}>
                No observed holidays found for {selectedYear}.
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
                {holidays.map((holiday, index) => {
                  const formatDate = (dateStr) => {
                    try {
                      // Parse as local date to avoid timezone issues
                      // Split the date string and create a local date
                      const [dateYear, month, day] = dateStr.split('-').map(num => parseInt(num, 10));
                      const date = new Date(dateYear, month - 1, day); // month is 0-indexed
                      const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' });
                      const monthDay = date.toLocaleDateString('en-US', { 
                        month: 'long', 
                        day: 'numeric' 
                      });
                      return `${dayOfWeek}, ${monthDay}`;
                    } catch {
                      return dateStr;
                    }
                  };
                  
                  return (
                    <div
                      key={index}
                      style={{
                        display:'flex',
                        justifyContent:'space-between',
                        alignItems:'center',
                        flexWrap:'wrap',
                        gap:'4px',
                        padding:'12px 0',
                        borderBottom: index < holidays.length - 1 ? '1px solid #f3f4f6' : 'none'
                      }}
                    >
                      <div style={{ fontWeight:'600', fontSize:'16px' }}>
                        {formatDate(holiday.date)}
                      </div>
                      <div style={{ fontSize:'12px', color:'var(--symbolica-muted)' }}>
                        {holiday.policy}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PTOPage; 